import classes from '*.module.css';
import { LayoutActions, LayoutSelectors, MainLayoutArea } from 'layout';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Skeleton from '@material-ui/lab/Skeleton';

import { ZoneSelectors, ZoneThunks } from './duck';
import { useStyles } from './ZoneList.jss';

export interface ZoneListProps {}

const ZoneListSkeleton: React.FunctionComponent = React.memo(() => {
  const classes = useStyles();
  return (
    <div className={classes.skeletonContainer}>
      <Skeleton className={classes.skeletonItem} variant="text" width={160} />
      <Skeleton className={classes.skeletonItem} variant="text" width={140} />
      <Skeleton className={classes.skeletonItem} variant="text" width={190} />
    </div>
  );
});

export const ZoneList: React.FunctionComponent<ZoneListProps> = (props) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const history = useHistory();
  const classes = useStyles();
  useEffect(() => {
    dispatch(ZoneThunks.fetchZones());
  }, [dispatch]);
  const layoutState = useSelector(LayoutSelectors.selectLayoutState);

  const fetchingZones = useSelector(ZoneSelectors.selectFetchingZones);
  const zones = useSelector(ZoneSelectors.selectZones);

  useEffect(() => {
    if (!zones.overHead.length && zones.onHold.length && layoutState.selectedMainArea !== MainLayoutArea.ZonesOnHold) {
      dispatch(LayoutActions.selectMainArea(MainLayoutArea.ZonesOnHold, 0));
    } else if (
      zones.overHead.length &&
      !zones.onHold.length &&
      layoutState.selectedMainArea !== MainLayoutArea.ZonesOverhead
    ) {
      dispatch(LayoutActions.selectMainArea(MainLayoutArea.Unauthorized, 0));
    }
  }, [layoutState, zones]);

  const zoneList = useMemo(() => {
    if (zones.overHead.length && layoutState.selectedMainArea === MainLayoutArea.ZonesOverhead) {
      return zones.overHead;
    }

    if (zones.onHold.length && layoutState.selectedMainArea === MainLayoutArea.ZonesOnHold) {
      return zones.onHold;
    }

    if (!zones.overHead.length && zones.onHold.length) {
      return zones.onHold;
    }

    if (!zones.onHold.length && zones.overHead.length) {
      return zones.overHead;
    }

    return [];
  }, [layoutState, zones]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const id = Number(e.currentTarget.id);

      const zone = zoneList.find((z) => z.zoneId === id);
      if (!zone) {
        return;
      }
      dispatch(LayoutActions.selectMainArea(layoutState.selectedMainArea, id));
      history.push(`/zone/${id}`);
    },
    [zones, history, zoneList, layoutState.selectedMainArea]
  );

  const onTabsChanged = useCallback(
    (e: React.ChangeEvent<{}>, value: any) => {
      dispatch(LayoutActions.selectMainArea(value as MainLayoutArea, 0));
    },
    [dispatch]
  );

  return (
    <div className={classes.container}>
      <Tabs value={layoutState.selectedMainArea} onChange={onTabsChanged}>
        {zones.overHead.length > 0 && <Tab label={t('overHead')} value={MainLayoutArea.ZonesOverhead} />}
        {zones.onHold.length > 0 && <Tab label={t('onHold')} value={MainLayoutArea.ZonesOnHold} />}
      </Tabs>
      {fetchingZones ? (
        <ZoneListSkeleton />
      ) : (
        <List className={classes.list}>
          {zoneList.map((z) => (
            <ListItem id={z.zoneId.toString()} key={z.zoneId} button divider onClick={handleClick}>
              <ListItemText primary={z.zoneName} />
            </ListItem>
          ))}
        </List>
      )}
    </div>
  );
};
